import * as APITypes from "~/API";

/** Boolean listing attributes shared by Unit and UnitPlan in the schema. */
export const LISTING_FEATURE_KEYS = [
  "inUnitLaundry",
  "dishwasher",
  "airConditioning",
  "furnished",
  "balcony",
  "petFriendly",
] as const;

export type ListingFeatureKey = (typeof LISTING_FEATURE_KEYS)[number];

export type ListingFeatures = Pick<APITypes.Unit, ListingFeatureKey>;

export type PlanListingCard = {
  kind: "plan";
  id: string;
  name: string;
  propertyID: string;
  propertyName: string;
  city: string;
  street: string;
  numBedroom: string;
  numBathroom: string;
  size: string;
  rentMin: number;
  rentMax: number;
  availableCount: number;
  coverImageUrl: string | null;
  features: ListingFeatures;
};

export type UnitListingCard = {
  kind: "unit";
  id: string;
  name: string;
  propertyID: string;
  propertyName: string;
  city: string;
  street: string;
  numBedroom: string;
  numBathroom: string;
  size: string;
  rent: number;
  availabilityDate: string | null;
  coverImageUrl: string | null;
  features: ListingFeatures;
};

export type ListingCard = PlanListingCard | UnitListingCard;

export type ListingPropertyOption = {
  id: string;
  name: string;
};

export type ListingBrowseLoaderResult = {
  items: ListingCard[];
  total: number;
  page: number;
  pageSize: number;
  propertyOptions: ListingPropertyOption[];
};

/** Property summary for the tenant property details page header. */
export type ListingPropertyDetailsInfo = {
  id: string;
  name: string;
  description: string | null;
  street: string;
  city: string;
  state: string;
  country: string;
  zipCode: string;
  poBox: string | null;
  areaSize: string | null;
  contact: string | null;
  amenities: ListingAmenitySummary[];
};

/** Loader data for global or property-scoped listing browse pages. */
export type ListingBrowsePageLoaderResult = ListingBrowseLoaderResult & {
  lockedPropertyId?: string;
  propertyName?: string;
  propertyInfo?: ListingPropertyDetailsInfo;
};

export const LISTING_PLACEHOLDER_IMAGE = "/images/listing-placeholder.svg";

export const LISTING_PAGE_SIZE = 10;

export const LISTING_FEATURE_LABEL_KEYS: Record<ListingFeatureKey, string> = {
  inUnitLaundry: "Listing_feature_in_unit_laundry",
  dishwasher: "Listing_feature_dishwasher",
  airConditioning: "Listing_feature_air_conditioning",
  furnished: "Listing_feature_furnished",
  balcony: "Listing_feature_balcony",
  petFriendly: "Listing_feature_pet_friendly",
};

/** Query param keys forwarded to listing-list loader. */
export type ListingPropertyDetails = {
  id: string;
  name: string;
  description: string | null;
  city: string;
  state: string;
  country: string;
  zipCode: string;
};

export type ListingAmenitySummary = {
  id: string;
  name: string;
  description: string | null;
  locationDescription: string | null;
};

export type ListingAvailableUnitRow = {
  id: string;
  name: string;
  rent: number;
  availabilityDate: string | null;
};

export type ListingTenantContext = {
  status: APITypes.TenantStatusType;
};

export type PlanListingDetails = {
  kind: "plan";
  id: string;
  name: string;
  description: string | null;
  numBedroom: string;
  numBathroom: string;
  size: string;
  rentMin: number;
  rentMax: number;
  property: ListingPropertyDetails;
  features: ListingFeatures;
  imageUrls: string[];
  amenities: ListingAmenitySummary[];
  availableUnits: ListingAvailableUnitRow[];
  tenant: ListingTenantContext;
};

export type UnitListingDetails = {
  kind: "unit";
  id: string;
  name: string;
  description: string | null;
  numBedroom: string;
  numBathroom: string;
  size: string;
  rent: number;
  availabilityDate: string | null;
  property: ListingPropertyDetails;
  features: ListingFeatures;
  imageUrls: string[];
  amenities: ListingAmenitySummary[];
  tenant: ListingTenantContext;
};

export type ListingDetailsResult = PlanListingDetails | UnitListingDetails;

export type ListingDetailsKind = "plan" | "unit";

export const LISTING_LIST_QUERY_KEYS = [
  "page",
  "propertyId",
  "location",
  "numBedroom",
  "numBathroom",
  ...LISTING_FEATURE_KEYS,
] as const;
